import {setConsumerProductsInfo, updateConsumerProductsInfo} from '../../state/ducks/consumer';
import {addSignedMedia} from '../../state/ducks/media';
import {
    makeSelectConsumerProductsMergedInfo,
    selectConsumerProducts,
    selectConsumerProductsInfo,
} from '../../state/selectors/consumer';
import {dispatch, getState} from '../../state/store';
import arrayUtils from '../../utils/arrayUtils';
import backendService from '../app/backendService';
import cryptoService from '../cryptoService';
import deviceAssetService from '../device/deviceAssetService';
import * as gigyaService from '../gigya/gigyaService';
import marketTypeService from '../marketTypeService';
import mediaService from '../mediaService';
import productHelpers from '../product/productHelpers';
import iccMarketService from './iccMarketService';

const MAX_PRODUCTS_COUNT_PER_ONE_REQUEST = 10;

const getDetailedProducts = async (throwException = false) => {
    const isDCE2 = marketTypeService.isDCE2();
    const isGamBackend = backendService.isGamBackend();

    if (isGamBackend && isDCE2) return;

    const gigyaToken = gigyaService.getGigyaToken(true);
    const consumerProducts = await iccMarketService.getConsumerProducts(gigyaToken);

    if (isDCE2) return;

    await getProductsInfoByConsumerProducts(consumerProducts, throwException);
    await getProductsAssetsByProducts();
};

const getProductsInfoByConsumerProducts = async (consumerProducts, throwException) => {
    if (!consumerProducts) return;

    const codentifyIds = consumerProducts
        .filter(({codentify}) => productHelpers.isOriginalCodentifyValid(codentify))
        .map(({codentify}) => codentify);

    const encryptedProductsIds = consumerProducts
        .filter(({codentify}) => !codentifyIds.includes(codentify))
        .map(({deviceSerialNumber}) => cryptoService.encryptDeviceSerialNumber(deviceSerialNumber))
        .filter((id) => id);

    await getProductsInfo({productsIds: codentifyIds, throwException, isCodentify: true, overrideProducts: true});
    await getProductsInfo({productsIds: encryptedProductsIds, throwException});
};

const getProductsInfo = async ({productsIds, throwException, isCodentify, overrideProducts}) => {
    if (!productsIds) return;

    const productsIdsSplitted = arrayUtils.splitArray(productsIds, MAX_PRODUCTS_COUNT_PER_ONE_REQUEST);

    const productsInfoRequestsList = productsIdsSplitted.map((consumerProductsIdsChunk) =>
        isCodentify
            ? iccMarketService.getProductInfoListV2({codentifyIds: consumerProductsIdsChunk, throwException})
            : iccMarketService.getProductInfoList({serialNumbers: consumerProductsIdsChunk, throwException})
    );

    return Promise.all(productsInfoRequestsList).then((productsInfoList) => {
        const productsInfoListMerged = [].concat(...productsInfoList);
        const productsInfoFiltered = productsInfoListMerged.filter((productInfo) => !!productInfo);

        if (overrideProducts) {
            dispatch(setConsumerProductsInfo(productsInfoFiltered));
        } else {
            dispatch(updateConsumerProductsInfo(productsInfoFiltered));
        }

        return productsInfoListMerged;
    });
};

const getDetailedProductsInfoByCodentifyIds = async (codentifyIds, throwException) => {
    if (!codentifyIds?.length) return;

    return getProductsInfo({productsIds: codentifyIds, throwException, isCodentify: true});
};

const getDetailedProductsInfoBySerialNumbers = async (deviceSerialNumbers, throwException) => {
    if (!deviceSerialNumbers?.length) return;

    const encryptedProductsIds = deviceSerialNumbers
        .map((deviceSerialNumber) => cryptoService.encryptDeviceSerialNumber(deviceSerialNumber))
        .filter((id) => id);

    return getProductsInfo({productsIds: encryptedProductsIds, throwException});
};

const getDetailedProduct = async (deviceSerialNumber, throwException) => {
    const state = getState();
    const consumerProducts = selectConsumerProducts(state);

    if (!consumerProducts) return;

    const consumerProduct = consumerProducts.find((device) => device.deviceSerialNumber === deviceSerialNumber);

    if (!consumerProduct) return;

    const encryptedProductSerialNumber = cryptoService.encryptDeviceSerialNumber(consumerProduct.deviceSerialNumber);
    const productInfoList = await iccMarketService.getProductInfoList({
        serialNumbers: [encryptedProductSerialNumber],
        throwException,
    });
    const productInfo = productInfoList[0];

    if (!productInfo) return;

    const consumerProductsInfoList = selectConsumerProductsInfo(state);
    const consumerProductIndex = consumerProductsInfoList.findIndex(
        (product) => product.deviceSerialNumber === productInfo.deviceSerialNumber
    );

    if (consumerProductIndex >= 0) {
        consumerProductsInfoList.splice(consumerProductIndex, 1);
    }

    consumerProductsInfoList.push(productInfo);

    dispatch(setConsumerProductsInfo(consumerProductsInfoList));
};

const getProductsAssetsByProducts = async () => {
    const consumerProducts = makeSelectConsumerProductsMergedInfo()(getState());

    if (!consumerProducts) return [];

    const mediaNamesList = consumerProducts.map(({mediaId}) => mediaId);
    const mediaNamesWithThumbnailsList = deviceAssetService.getDeviceAssetsList(mediaNamesList);

    return getProductsAssets(mediaNamesWithThumbnailsList);
};

const getProductsAssets = async (mediaNames) => {
    const assetsFromState = mediaService.getSignedMediaList();
    const mediaNamesList = Array.isArray(mediaNames) ? arrayUtils.unique(mediaNames) : [mediaNames];
    const mediaNamesToSign = mediaNamesList.filter(
        (mediaName) => !assetsFromState.some((asset) => asset.mediaId === mediaName && !asset.isExpired())
    );

    if (mediaNamesToSign.length) {
        const productAssets = await iccMarketService.getMediaUrl({mediaNameList: mediaNamesToSign});

        if (productAssets) {
            dispatch(addSignedMedia(productAssets));
        } else {
            dispatch(setConsumerProductsInfo([]));
        }

        return productAssets;
    } else {
        return [];
    }
};

export default {
    getDetailedProducts,
    getProductsAssetsByProducts,
    getProductsAssets,
    getDetailedProduct,
    getDetailedProductsInfoByCodentifyIds,
    getDetailedProductsInfoBySerialNumbers,
};
